import React, { useState } from 'react';
import { useToolStore, ToolId } from '../../store/useToolStore';
import { Layers, Image as ImageIcon, Paintbrush, ScanEye, FileText, Sparkles, LucideIcon } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { APP_VERSION } from '../../config/version';

// Tool Components
import { UniversalToolbar } from '../tools/UniversalToolbar';


type Tab = 'properties' | 'assets';

const RightPanel: React.FC = () => {
    const [activeTab, setActiveTab] = useState<Tab>('properties');
    const { activeTool, setActiveTool, isPanelOpen, setPanelOpen } = useToolStore();
    const navigate = useNavigate();

    // Responsive Initialization
    React.useEffect(() => {
        const checkMobile = () => {
            if (window.innerWidth < 768) {
                setPanelOpen(false);
            } else {
                setPanelOpen(true);
            }
        };

        // Check on mount
        checkMobile();

        // Optional: Listen for resize if dynamic behavior is desired
        // window.addEventListener('resize', checkMobile);
        // return () => window.removeEventListener('resize', checkMobile);
    }, [setPanelOpen]);

    const location = useLocation();
    const basePath = location.pathname.startsWith('/trial') ? '/trial' : '/app';

    const tools: { id: ToolId; icon: LucideIcon; label: string; path: string }[] = [
        { id: 'render-bridge', icon: Paintbrush, label: 'RenderBridge', path: `${basePath}/render-bridge` },
        { id: 'lens', icon: ScanEye, label: 'Lens', path: `${basePath}/lens` },
        { id: 'magic-touch', icon: FileText, label: 'Magic Touch', path: `${basePath}/magic-touch` },
        { id: 'refiner', icon: Sparkles, label: 'Refiner', path: `${basePath}/refiner` },
        { id: 'gallery', icon: ImageIcon, label: 'Gallery', path: `${basePath}/gallery` },
    ];

    const handleToolClick = (toolId: ToolId, path: string) => {
        setActiveTool(toolId);
        navigate(path);
        // On mobile, auto-close after selection if we want, but keeping it open for now is standard behavior
    };

    const renderProperties = () => {
        return <UniversalToolbar />;
    };

    const renderAssets = () => (
        <div className="space-y-8">
            {/* Layers Section */}
            <div>
                <div className="flex items-center space-x-2 text-zinc-500 mb-3 px-1">
                    <Layers size={14} />
                    <span className="text-xs font-medium uppercase tracking-widest">Layers</span>
                </div>
                <div className="bg-zinc-900/30 border border-dashed border-zinc-800 rounded-xl p-6 text-center">
                    <p className="text-xs text-zinc-600">No layers generated yet.</p>
                    <p className="text-[10px] text-zinc-700 mt-1">Layers appear here after processing.</p>
                </div>
            </div>

            {/* Reference Bank Section */}
            <div>
                <div className="flex items-center space-x-2 text-zinc-500 mb-3 px-1">
                    <ImageIcon size={14} />
                    <span className="text-xs font-medium uppercase tracking-widest">Reference Bank</span>
                </div>
                <button className="w-full py-4 border border-dashed border-zinc-700 rounded-xl text-zinc-500 text-xs font-medium hover:bg-zinc-900 hover:border-zinc-500 hover:text-zinc-300 transition-all flex items-center justify-center gap-2">
                    <span className="text-lg">+</span>  Upload Reference
                </button>
            </div>
        </div>
    );

    if (!isPanelOpen) return null;

    return (
        <div className="2xl:w-[420px] xl:w-96 lg:w-80 md:w-72 w-full sm:w-[400px] absolute h-full md:relative right-0 bg-black border-l border-zinc-800 flex shadow-2xl z-40 shrink-0 transition-all duration-300">
            {/* Sidebar Navigation Strip */}
            <div className="w-[60px] lg:w-[50px] flex flex-col items-center py-6 space-y-6 border-r border-zinc-800/50 bg-black/50 transition-all duration-300">
                {tools.map((tool) => (
                    <button
                        key={tool.id}
                        onClick={() => handleToolClick(tool.id, tool.path)}
                        className={`p-3 rounded-xl transition-all duration-300 group relative ${activeTool === tool.id
                            ? 'bg-zinc-800 text-white shadow-lg shadow-zinc-900/50'
                            : 'text-zinc-600 hover:text-zinc-400 hover:bg-zinc-900/50'
                            } `}
                        title={tool.label}
                    >
                        <tool.icon size={22} strokeWidth={1.5} />
                        {activeTool === tool.id && (
                            <div className="absolute -right-[1px] top-1/2 transform -translate-y-1/2 w-1 h-6 bg-white rounded-l-full shadow-[0_0_10px_rgba(255,255,255,0.5)]"></div>
                        )}
                    </button>
                ))}
            </div>

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col min-w-0">
                {/* Header / Tabs */}
                <div className="flex items-center justify-between p-2 pb-0 pt-4 px-4 bg-black/50 backdrop-blur-md sticky top-0 z-10 select-none">
                    <div className="flex space-x-6 w-full border-b border-zinc-800">
                        <button
                            onClick={() => setActiveTab('properties')}
                            className={`pb-3 text-sm font-medium transition-all relative px-1 ${activeTab === 'properties' ? 'text-white' : 'text-zinc-500 hover:text-zinc-300'
                                }`}
                        >
                            Properties
                            {activeTab === 'properties' && (
                                <div className="absolute bottom-0 left-0 w-full h-[2px] bg-white shadow-[0_0_10px_rgba(255,255,255,0.5)] rounded-full"></div>
                            )}
                        </button>
                        <button
                            onClick={() => setActiveTab('assets')}
                            className={`pb-3 text-sm font-medium transition-all relative px-1 ${activeTab === 'assets' ? 'text-white' : 'text-zinc-500 hover:text-zinc-300'
                                }`}
                        >
                            Assets & Layers
                            {activeTab === 'assets' && (
                                <div className="absolute bottom-0 left-0 w-full h-[2px] bg-white shadow-[0_0_10px_rgba(255,255,255,0.5)] rounded-full"></div>
                            )}
                        </button>
                    </div>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto custom-scrollbar">
                    <div className="p-6">
                        {activeTab === 'properties' ? renderProperties() : renderAssets()}
                    </div>
                </div>

                {/* Action Footer */}
                <div className="p-4 border-t border-zinc-900 bg-black/95">
                    <div className="flex items-center justify-between text-[10px] text-zinc-600">
                        <span>Nano Banana Engine v{APP_VERSION}</span>
                        <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></div>
                            <span className="text-emerald-500 font-medium">Online</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RightPanel;
